import { Alert, Button, Form, Input, Select } from "antd";
import { useForm } from "antd/lib/form/Form";
import * as React from "react";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { AccountBean, TimeZoneBean } from "src/api/api";
import accountApi from "./AccountApi";
import { formDataLoader, FormDataType } from "../../../util/FormDataLoader";
import { isAdmin } from "../../../util/Util";
import { useUser } from "../../../context/UserContext";

const FormItem = Form.Item;
const Option = Select.Option;

interface Props {}

const formInfoItems: FormDataType[] = [
  FormDataType.DspList,
  FormDataType.Publishers,
  FormDataType.TimeZones,
];

const AccountEdit: React.FC<Props> = () => {
  const { user } = useUser();

  const [info, setInfo] = useState({
    dspList: [],
    publishers: [],
    timezones: [],
  });

  const [form] = useForm();
  const [error, setError] = useState();
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const [roleState, setRole] = useState<AccountBean.RoleEnum>();

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      accountApi
        .edit(Number.parseInt(id ?? "-1"), { ...values })
        .then(history.goBack)
        .catch((err) => {
          setError(err.error);
        });
    });
  };

  const admin = isAdmin(user);

  const loadData = async () => {
    try {
      if (user) {
        let response: any = await accountApi.view(id);
        let formInfo = admin
          ? await formDataLoader(...formInfoItems)
          : await formDataLoader(FormDataType.TimeZones);
        setInfo(formInfo as any);
        form.setFieldsValue(response.data);
        setRole(response.data.role);
      }
    } catch (e) {
      onRejected(e);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  const onRejected = (err) => {
    setError(err.error);
  };

  const handleRoleChange = (value) => {
    setRole(value);
  };

  return (
    <Form
      style={{ maxWidth: "1000px" }}
      onFinish={handleSubmit}
      form={form}
      labelCol={{ span: 5 }}
      wrapperCol={{ span: 19 }}
    >
      {error ? <Alert message={error} type="error" /> : null}
      <FormItem
        name="username"
        rules={[{ required: true, message: "Please input Account name" }]}
        label="Name"
      >
        <Input readOnly={!admin} />
      </FormItem>
      <FormItem
        name="email"
        rules={[
          { required: true, message: "Please input Account email" },
          {
            pattern: new RegExp(".+@.+\\..+"),
            message: "Email is incorrect",
          },
        ]}
        label="Email"
      >
        <Input />
      </FormItem>
      {admin && (
        <FormItem
          name="role"
          rules={[{ required: true, message: "Please select Account Role" }]}
          label="Role"
        >
          <Select
            disabled={
              roleState === AccountBean.RoleEnum.Publisher ||
              roleState === AccountBean.RoleEnum.Dsp
            }
            onChange={handleRoleChange}
            placeholder="Select Role"
          >
            <Option
              key={AccountBean.RoleEnum.Admin}
              value={AccountBean.RoleEnum.Admin}
            >
              {AccountBean.RoleEnum.Admin}
            </Option>
            <Option
              key={AccountBean.RoleEnum.Reporting}
              value={AccountBean.RoleEnum.Reporting}
            >
              {AccountBean.RoleEnum.Reporting}
            </Option>
          </Select>
        </FormItem>
      )}
      <FormItem
        name="walletId"
        label="Wallet ID"
        rules={[
          {
            pattern: new RegExp("^\\s*0x\\S*\\s*$"),
            message: "Please input correct Wallet ID",
          },
        ]}
      >
        <Input type="text" autoComplete="off" />
      </FormItem>
      <FormItem name="publicValidationKey" label="Public validation key">
        <Input />
      </FormItem>
      <FormItem
        name="timezoneId"
        rules={[
          {
            required: true,
            message: "Please select Time Zone",
            type: "string",
          },
        ]}
        label="Time Zone"
      >
        <Select disabled={!admin} showSearch placeholder="Select time zone">
          {admin &&
            info.timezones.map((timezone: TimeZoneBean) => (
              <Option key={timezone.id} value={timezone.id as string}>
                {timezone.id}
              </Option>
            ))}
        </Select>
      </FormItem>
      <FormItem
        name="password"
        label="Password"
        rules={[
          {
            pattern: new RegExp(
              "(?=.*[0-9])(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z!@#$%^&*]{12,}"
            ),
            message:
              "Use 12 characters or more for your password. It should include at least one character from each group: a-z, A-Z, 0-9, !@#$%^&*",
          },
          {
            max: 255,
            message: "Password cannot be longer that 255 characters",
          },
        ]}
      >
        <Input
          autoComplete="new-password"
          placeholder="Leave blank so you don't change"
          type="password"
        />
      </FormItem>
      <FormItem wrapperCol={{ offset: 11, span: 6 }}>
        <Button
          style={{ width: "45%", margin: "0 2%" }}
          type="primary"
          htmlType="submit"
        >
          Save
        </Button>
        <Button
          style={{ width: "45%", margin: "0 2%" }}
          type="primary"
          danger
          htmlType="button"
          onClick={history.goBack}
        >
          Cancel
        </Button>
      </FormItem>
    </Form>
  );
};

export default AccountEdit;
